/**
 * CountryPicker — one visual language for choosing a market across Tago.
 *
 * It intentionally shares the flag, sheet, row, selection and touch-target
 * conventions of CountryCodePicker, without coupling a market selector to
 * telephone dial codes.
 */
import { useCallback, useMemo, useState } from 'react'
import { Platform, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { AppModal } from './AppModal'
import { GlassSheet } from './GlassSheet'
import { CountryFlag } from './CountryFlag'
import { Pressable } from './Pressable'
import { TrackedNativePressable as RNPressable } from './TrackedNativePressable'
import { Check, ChevronDown, X } from './icons'
import { selection } from '@/lib/utils/haptics'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { INTERACTION, Z_INDEX, useSemanticColors } from '@/lib/theme/tokens'
import { COLUMN_PRESETS } from '@/lib/theme/desktop-spacing'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { DEFAULT_COUNTRY_ISO2, findCountry, type Country } from '@/lib/data/countries'

interface CountryPickerProps {
  value: string
  onChange: (iso2: string) => void
  countries: readonly Country[]
  testID?: string
}

function selectedWash(primary: string, isDark: boolean): string {
  if (primary.startsWith('#') && primary.length === 7) {
    return isDark ? `${primary}22` : `${primary}14`
  }
  return isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
}

export function CountryPicker({ value, onChange, countries, testID }: CountryPickerProps) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { isDesktop } = useBreakpoint()
  const [open, setOpen] = useState(false)
  const locale: 'es' | 'en' = i18n.language.toLowerCase().startsWith('en') ? 'en' : 'es'
  const selected =
    countries.find((country) => country.iso2 === value) ??
    findCountry(value) ??
    countries[0] ??
    findCountry(DEFAULT_COUNTRY_ISO2)!
  const selectedName = locale === 'es' ? selected.nameEs : selected.nameEn

  const options = useMemo(() => {
    const homeFirst = [...countries]
    homeFirst.sort((a, b) => {
      if (a.iso2 === DEFAULT_COUNTRY_ISO2) return -1
      if (b.iso2 === DEFAULT_COUNTRY_ISO2) return 1
      return (locale === 'es' ? a.nameEs : a.nameEn).localeCompare(
        locale === 'es' ? b.nameEs : b.nameEn,
      )
    })
    return homeFirst
  }, [countries, locale])

  const close = useCallback(() => setOpen(false), [])
  const choose = useCallback(
    (iso2: string) => {
      selection()
      close()
      onChange(iso2)
    },
    [close, onChange],
  )

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={t('tours.catalog_country', { country: selectedName })}
        accessibilityState={{ expanded: open }}
        testID={testID}
        style={({ pressed }) => ({
          minHeight: INTERACTION.minTouchTarget,
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          gap: SPACING.sm,
          paddingHorizontal: SPACING.md,
          borderRadius: RADIUS.full,
          borderWidth: 1,
          borderColor: colors.border,
          backgroundColor: colors.surface,
          opacity: pressed ? 0.72 : 1,
        })}
      >
        <CountryFlag iso2={selected.iso2} width={24} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textPrimary }}>
          {selectedName}
        </Text>
        <ChevronDown size={15} color={colors.textSecondary} strokeWidth={2} />
      </Pressable>

      <AppModal
        visible={open}
        animationType={isDesktop ? 'fade' : 'slide'}
        transparent
        presentationStyle="overFullScreen"
        statusBarTranslucent
        onRequestClose={close}
      >
        <View
          style={{
            flex: 1,
            justifyContent: isDesktop ? 'center' : 'flex-end',
            alignItems: isDesktop ? 'center' : undefined,
            paddingHorizontal: isDesktop ? SPACING.lg : 0,
          }}
        >
          <RNPressable
            style={
              isDesktop
                ? { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }
                : { flex: 1 }
            }
            onPress={close}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
          />
          <GlassSheet
            style={{
              width: '100%',
              maxWidth: isDesktop
                ? COLUMN_PRESETS.form
                : Platform.OS === 'web'
                  ? COLUMN_PRESETS.formWide
                  : undefined,
              alignSelf: 'center',
              zIndex: Z_INDEX.modal,
              borderTopLeftRadius: RADIUS.xl,
              borderTopRightRadius: RADIUS.xl,
              borderBottomLeftRadius: isDesktop ? RADIUS.xl : 0,
              borderBottomRightRadius: isDesktop ? RADIUS.xl : 0,
            }}
            contentStyle={{
              paddingTop: SPACING.md,
              paddingBottom: isDesktop ? SPACING.md : Math.max(insets.bottom, SPACING.lg),
            }}
            testID={testID ? `${testID}.sheet` : 'country-picker.sheet'}
          >
            {isDesktop ? null : (
              <View
                style={{
                  width: 36,
                  height: 4,
                  alignSelf: 'center',
                  marginBottom: SPACING.md,
                  borderRadius: 2,
                  backgroundColor: colors.border,
                }}
              />
            )}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: SPACING.lg,
                marginBottom: SPACING.sm,
              }}
            >
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 18,
                  lineHeight: 24,
                  letterSpacing: -0.2,
                  color: colors.textPrimary,
                }}
              >
                {t('tours.catalog_country_title')}
              </Text>
              <Pressable
                onPress={close}
                accessibilityRole="button"
                accessibilityLabel={t('common.close')}
                hitSlop={12}
                style={{
                  width: INTERACTION.minTouchTarget,
                  height: INTERACTION.minTouchTarget,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={22} color={colors.textSecondary} />
              </Pressable>
            </View>
            {options.map((country) => {
              const isSelected = country.iso2 === selected.iso2
              const name = locale === 'es' ? country.nameEs : country.nameEn
              return (
                <RNPressable
                  key={country.iso2}
                  onPress={() => choose(country.iso2)}
                  accessibilityRole="button"
                  accessibilityLabel={name}
                  accessibilityState={{ selected: isSelected }}
                  testID={`${testID ?? 'country-picker'}.option.${country.iso2}`}
                  style={({ pressed }) => ({
                    minHeight: INTERACTION.minTouchTarget + SPACING.sm,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.md,
                    paddingHorizontal: SPACING.lg,
                    paddingVertical: SPACING.sm,
                    backgroundColor: isSelected
                      ? selectedWash(colors.primary, isDark)
                      : pressed
                        ? colors.surfaceSubtle
                        : 'transparent',
                  })}
                >
                  <CountryFlag iso2={country.iso2} width={28} />
                  <Text
                    style={{
                      flex: 1,
                      fontFamily: isSelected ? FONTS.bodyBold : FONTS.body,
                      fontSize: 16,
                      color: colors.textPrimary,
                    }}
                  >
                    {name}
                  </Text>
                  {isSelected ? (
                    <Check size={18} color={colors.primary} strokeWidth={2.4} />
                  ) : (
                    <View style={{ width: 18 }} />
                  )}
                </RNPressable>
              )
            })}
          </GlassSheet>
        </View>
      </AppModal>
    </>
  )
}
